import { Globe } from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { SUPPORTED_LANGUAGES  } from '../../i18n';
import type {SupportedLanguageCode} from '../../i18n';

const DROPDOWN_CLASS = 'language-selector-dropdown';

interface LanguageSelectorProps {
  /** Use compact styling (e.g. same as theme toggle in dashboard header). */
  variant?: 'default' | 'compact';
  /** Optional class for the trigger button. */
  className?: string;
  /** Scoped class when in landing nav (e.g. for scrolled vs transparent). */
  buttonClass?: string;
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  variant = 'default',
  className = '',
  buttonClass = '',
}) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [open]);

  const handleSelect = (code: SupportedLanguageCode) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  const baseButtonClass =
    variant === 'compact'
      ? 'p-2 rounded-lg vscode-bg-tertiary hover:vscode-bg-hover transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation'
      : 'p-2 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation';

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`${baseButtonClass} ${buttonClass}`}
        aria-label={t('layout.language')}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="language-selector"
      >
        <Globe
          className={`w-5 h-5 ${variant === 'compact' ? 'text-violet-500 dark:text-violet-400' : 'currentColor'}`}
          aria-hidden
        />
      </button>
      {open && (
        <div
          className={`absolute right-0 mt-2 min-w-[10rem] vscode-bg-secondary border vscode-border rounded-lg shadow-xl py-2 z-[1002] ${DROPDOWN_CLASS}`}
          role="listbox"
          aria-label={t('layout.language')}
        >
          {SUPPORTED_LANGUAGES.map(({ code, label }) => (
            <button
              key={code}
              role="option"
              aria-selected={i18n.language === code}
              onClick={() => handleSelect(code)}
              className={`w-full px-4 py-2 text-left text-sm transition-colors flex items-center justify-between ${
                i18n.language === code
                  ? 'vscode-bg-tertiary vscode-text-primary font-bold'
                  : 'vscode-text-secondary hover:vscode-bg-tertiary hover:vscode-text-primary'
              }`}
            >
              <span>{label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
